import type { SelectOptions } from 'app/components/elements/InputSelect';

export type CriticalityLevel = {
  key: string;
  label: string;
  color: string;
  sortOrder: number;
};

const MUI_COLOR_TOKENS: Record<string, string> = {
  'error.main': '#dc2626',
  'warning.main': '#ea580c',
  'success.main': '#16a34a',
  'text.secondary': '#6b7280'
};

/** Resolve config color token or hex for use in CSS/MenuItem backgrounds. */
export function resolveCriticalityCssColor(color?: string, fallback = '#f3f4f6'): string {
  if (!color) return fallback;
  if (color.startsWith('#')) return color;
  return MUI_COLOR_TOKENS[color] || color;
}

export function toCriticalitySelectOptions(
  levels: CriticalityLevel[] = DEFAULT_CRITICALITY_LEVELS,
  emptyOption?: { label: string; value?: string }
): SelectOptions[] {
  const mapped = levels.map((l) => ({
    label: l.label,
    value: l.key,
    color: resolveCriticalityCssColor(l.color)
  }));
  if (emptyOption) {
    return [{ label: emptyOption.label, value: emptyOption.value ?? '' }, ...mapped];
  }
  return mapped;
}

export const DEFAULT_CRITICALITY_LEVELS: CriticalityLevel[] = [
  { key: 'low', label: 'Low', color: 'success.main', sortOrder: 0 },
  { key: 'medium', label: 'Medium', color: 'warning.main', sortOrder: 1 },
  { key: 'high', label: 'High', color: '#ea580c', sortOrder: 2 },
  { key: 'critical', label: 'Critical', color: 'error.main', sortOrder: 3 }
];

export type CriticalityDisplay = {
  key?: string;
  label?: string;
  color?: string;
} | null;

export function getCriticalityDisplay(
  criticalityKey: string | null | undefined,
  levels: CriticalityLevel[] = DEFAULT_CRITICALITY_LEVELS
): CriticalityDisplay {
  if (!criticalityKey) return null;
  const match = levels.find((l) => l.key === criticalityKey);
  if (!match) return { key: criticalityKey, label: criticalityKey, color: 'text.secondary' };
  return { key: match.key, label: match.label, color: match.color };
}

/** Map legacy integer asset criticality (1–5) to config key. */
export function legacyIntToCriticalityKey(value: number | null | undefined): string | undefined {
  if (value == null) return undefined;
  if (value <= 1) return 'low';
  if (value === 2) return 'medium';
  if (value === 3) return 'high';
  return 'critical';
}
